import { h } from "../ui/dom";
import { fmtKm, km, type LatLng } from "../game/geo";
import { distanceScore, per1000, PIN_MAX } from "../game/scoring";
import { t } from "../strings";
import type { Playable } from "../game/derive";
import type { Credits } from "../game/knowledge";
import type { Line, Overlay, Pin } from "../map/MapView";
import { tapToExplain, type Host, type RoundModule, type RoundState } from "./types";
import { COUNT_MS, revealLayers, spotOn } from "./reveal";
import { routeRows } from "./trail";

/**
 * Next stop (DECISIONS 2026-09-30): a journey is drawn part-way; the player drops a pin where it went next,
 * scored on the same distance table as Pin it. No options.
 */
type P = Extract<Playable, { type: "next" }>;
interface S extends RoundState {
  /** The one pin: a tap elsewhere moves it, there is never a second. */
  guess: LatLng | null;
  dist: number;
  /** Reveal progress after Lock in (0 → 1), as in Pin it; the rest of the journey is drawn at 1. */
  t: number;
}

/** Pin ids: route stops 100+, the answer 200 (tap after the round for the step note). */
const STOP = 100;
const ANSWER = 200;

const credits = (p: P, known: boolean): Credits => {
  const c: Credits = { n: known ? "k" : "l" };
  p.route.forEach((_, i) => (c[`s${i}`] = "l"));
  return c;
};


/** The journey so far while guessing; after Lock in, the reveal (two markers, growing line, rings) and then the whole journey. */
function overlay(host: Host, s: S, p: P): Overlay {
  const C = host.map.colors;
  const pins: Pin[] = p.route.map((r, i) => ({ ...r, id: STOP + i, text: String(i + 1), color: C.l1, label: r.name, sub: r.when ?? null, z: 1, tag: "route" }));
  const lines: Line[] = [{ pts: p.route.map((r) => [r.lat, r.lng] as [number, number]), color: C.l1, width: 2.4, tag: "route" }];
  if (!s.done || !s.guess) {
    if (s.guess) pins.push({ ...s.guess, kind: "guess", color: C.guess, z: 3, tag: "guess" });
    return { pins, lines, cities: p.cities };
  }
  const ans = p.answer;
  const rv = revealLayers(C, { guess: s.guess, answer: { ...ans, id: ANSWER, color: C.good, halo: true, label: ans.name, sub: ans.when ?? null }, km: s.dist, t: s.t });
  if (s.t >= 1) {
    const last = p.route[p.route.length - 1]!;
    lines.push({ pts: [[last.lat, last.lng], [ans.lat, ans.lng]], color: C.good, width: 2.4, dash: true, tag: "route" });
  }
  return { pins: [...pins, ...rv.pins], lines: [...lines, ...rv.lines], rings: rv.rings, cities: true };
}

export const next: RoundModule<P, S> = {
  init: () => ({ done: false, guess: null, dist: 0, t: 1 }),

  view(host, s, p, ms) {
    // While guessing: the journey so far, with room around it (the next stop may be far away).
    if (s.done && s.guess) host.map.fitPoints([...p.route, p.answer, s.guess], { ms, maxWorldPx: 30000 });
    else host.map.fitPoints(p.route, { ms, maxWorldPx: 6000 });
  },

  render(host, s, p) {
    const ans = p.answer;
    host.map.set(overlay(host, s, p));
    host.map.onPinClick = null;
    if (s.done) tapToExplain(host, { ...Object.fromEntries(p.route.map((r, i) => [STOP + i, r])), [ANSWER]: ans });
    host.map.onMapClick = s.done
      ? null
      : (ll) => {
          s.guess = ll;
          host.draw();
        };
    host.map.setPicking(!s.done);
    host.frame(
      p,
      h("p", { class: "clue" }, p.prompt),
      p.hint && !s.done ? h("p", { class: "hint" }, p.hint) : null,
      // No "tap the map" line before the pin (Egor, 30 Sep); once it is down, say it can be moved.
      !s.done && s.guess ? h("p", { class: "clue how" }, t.pin.moveHint) : null,
      s.done
        ? [routeRows([...p.route, p.answer], { label: t.next.route, mark: p.route.length, markText: t.next.answer, kind: "next" }), host.verdict()]
        : h(
            "div",
            { class: "row" },
            h(
              "button",
              {
                class: "btn",
                type: "button",
                "data-act": "submit",
                disabled: !s.guess,
                onclick: () => {
                  if (!s.guess) return;
                  s.dist = km(s.guess, ans);
                  s.t = 0;
                  const score = distanceScore(s.dist);
                  const word = t.ui.verdict(per1000(score, PIN_MAX));
                  const said = spotOn(s.dist) ? t.next.verdictSpotOn(word, ans.name) : t.next.verdict(word, ans.name, fmtKm(s.dist));
                  host.finish(score, { km: Math.round(s.dist), at: [Math.round(s.guess.lat * 1e4) / 1e4, Math.round(s.guess.lng * 1e4) / 1e4] }, said, credits(p, per1000(score, PIN_MAX) >= 600));
                  next.view(host, s, p, 900);
                  host.animate(COUNT_MS, (x) => {
                    s.t = x;
                    host.map.set(overlay(host, s, p));
                  });
                },
              },
              t.pin.lock,
            ),
          ),
    );
  },
};
